import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import DateQuickPresets from '../../components/DateQuickPresets';

const FAIXAS = [
  { key: 'a_vencer', label: 'A vencer' },
  { key: 'vencido_0_30', label: 'Vencido 0-30' },
  { key: 'vencido_31_60', label: 'Vencido 31-60' },
  { key: 'vencido_61_90', label: 'Vencido 61-90' },
  { key: 'vencido_90_plus', label: 'Vencido >90' },
];

const AgingPagarReceber = () => {
  const hojeIso = format(new Date(), 'yyyy-MM-dd');
  const [carregando, setCarregando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState({
    data_base: hojeIso,
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
    incluir_confirmados: false,
    visao: 'vencidos_e_a_vencer',
    meses_comparativo: 6,
    limite: 1000,
  });
  const [dados, setDados] = useState({
    resumo: {
      receber: {},
      pagar: {},
      total_receber: 0,
      total_pagar: 0,
      saldo_liquido: 0,
    },
    comparativo_mensal: [],
    itens: [],
    total_itens: 0,
    total_itens_base: 0,
  });

  const formatarValor = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros de aging:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/aging-pagar-receber', { params: filtros });
      const payload = response.data || {};
      setDados({
        resumo: payload.resumo || {
          receber: {},
          pagar: {},
          total_receber: 0,
          total_pagar: 0,
          saldo_liquido: 0,
        },
        comparativo_mensal: Array.isArray(payload.comparativo_mensal) ? payload.comparativo_mensal : [],
        itens: Array.isArray(payload.itens) ? payload.itens : [],
        total_itens: Number(payload.total_itens || 0),
        total_itens_base: Number(payload.total_itens_base || 0),
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar aging de pagar/receber');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const linhasFaixa = useMemo(
    () =>
      FAIXAS.map((faixa) => {
        const receber = Number(dados.resumo?.receber?.[faixa.key] || 0);
        const pagar = Number(dados.resumo?.pagar?.[faixa.key] || 0);
        return {
          ...faixa,
          receber,
          pagar,
          saldo: receber - pagar,
        };
      }),
    [dados.resumo]
  );
  const comparativoChart = useMemo(
    () =>
      (dados.comparativo_mensal || []).map((item) => ({
        ...item,
        label: `${String(item.mes).slice(5, 7)}/${String(item.mes).slice(2, 4)}`,
      })),
    [dados.comparativo_mensal]
  );

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Aging de Pagar/Receber</h1>
          <p className="text-gray-600">Envelhecimento por faixas de vencimento</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <DateQuickPresets
            mode="single"
            date={filtros.data_base}
            onSelect={({ date }) => setFiltros((old) => ({ ...old, data_base: date }))}
            className="mb-4"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
            <div>
              <label className="form-label">Data Base</label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_base}
                onChange={(e) => setFiltros((old) => ({ ...old, data_base: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Centro de Custo</label>
              <select
                className="form-select"
                value={filtros.centro_custo_id}
                onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}
              >
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select
                className="form-select"
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}
              >
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Visao</label>
              <select
                className="form-select"
                value={filtros.visao}
                onChange={(e) => setFiltros((old) => ({ ...old, visao: e.target.value }))}
              >
                <option value="vencidos_e_a_vencer">Vencidos + A vencer</option>
                <option value="somente_vencidos">Somente vencidos</option>
              </select>
            </div>
            <div>
              <label className="form-label">Meses comparativo</label>
              <input
                type="number"
                min="3"
                max="24"
                className="form-input"
                value={filtros.meses_comparativo}
                onChange={(e) => setFiltros((old) => ({ ...old, meses_comparativo: Number(e.target.value || 6) }))}
              />
            </div>
            <div>
              <label className="form-label">Limite de Itens</label>
              <input
                type="number"
                min="50"
                max="5000"
                className="form-input"
                value={filtros.limite}
                onChange={(e) => setFiltros((old) => ({ ...old, limite: Number(e.target.value || 1000) }))}
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={filtros.incluir_confirmados}
                  onChange={(e) => setFiltros((old) => ({ ...old, incluir_confirmados: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">Incluir confirmados</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Total a Receber</p>
            <p className="text-2xl font-bold text-success-700">R$ {formatarValor(dados.resumo.total_receber)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Total a Pagar</p>
            <p className="text-2xl font-bold text-danger-700">R$ {formatarValor(dados.resumo.total_pagar)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Saldo Liquido</p>
            <p className={`text-2xl font-bold ${Number(dados.resumo.saldo_liquido || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.resumo.saldo_liquido)}
            </p>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">Resumo por Faixa</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Faixa</th>
                  <th>Receber</th>
                  <th>Pagar</th>
                  <th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {linhasFaixa.map((linha) => (
                  <tr key={linha.key}>
                    <td>{linha.label}</td>
                    <td className="text-success-700">R$ {formatarValor(linha.receber)}</td>
                    <td className="text-danger-700">R$ {formatarValor(linha.pagar)}</td>
                    <td className={linha.saldo >= 0 ? 'text-success-700 font-semibold' : 'text-danger-700 font-semibold'}>
                      R$ {formatarValor(linha.saldo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Comparativo Mensal (base de lancamento)</h3>
        </div>
        <div className="card-body">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparativoChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
                <YAxis
                  yAxisId="bars"
                  stroke="#6b7280"
                  fontSize={12}
                  tickFormatter={(v) => `R$ ${(Number(v) / 1000).toFixed(0)}k`}
                />
                <YAxis yAxisId="line" orientation="right" stroke="#6b7280" fontSize={12} hide />
                <Tooltip formatter={(v) => [`R$ ${formatarValor(v)}`, 'Valor']} />
                <Legend />
                <Bar yAxisId="bars" dataKey="receber_total" name="Receber" fill="#16a34a" />
                <Bar yAxisId="bars" dataKey="pagar_total" name="Pagar" fill="#dc2626" />
                <Line yAxisId="line" type="monotone" dataKey="saldo_liquido" name="Saldo Liquido" stroke="#2563eb" strokeWidth={2} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th>Receber</th>
                  <th>Pagar</th>
                  <th>Receber Vencidos</th>
                  <th>Pagar Vencidos</th>
                  <th>Saldo Liquido</th>
                </tr>
              </thead>
              <tbody>
                {(dados.comparativo_mensal || []).map((item) => (
                  <tr key={item.mes}>
                    <td>{item.mes}</td>
                    <td className="text-success-700">R$ {formatarValor(item.receber_total)}</td>
                    <td className="text-danger-700">R$ {formatarValor(item.pagar_total)}</td>
                    <td className="text-success-700">R$ {formatarValor(item.receber_vencidos)}</td>
                    <td className="text-danger-700">R$ {formatarValor(item.pagar_vencidos)}</td>
                    <td className={Number(item.saldo_liquido || 0) >= 0 ? 'text-success-700 font-semibold' : 'text-danger-700 font-semibold'}>
                      R$ {formatarValor(item.saldo_liquido)}
                    </td>
                  </tr>
                ))}
                {(dados.comparativo_mensal || []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-6">Sem dados para comparativo</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Detalhamento ({dados.itens.length} de {dados.total_itens})</h3>
          <p className="text-xs text-gray-500 mt-1">Base total encontrada: {dados.total_itens_base}</p>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Descricao</th>
                  <th>Status</th>
                  <th>Dias</th>
                  <th>Faixa</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {(dados.itens || []).map((item) => (
                  <tr key={item.id}>
                    <td>{format(parseISO(item.data_lancamento), 'dd/MM/yyyy')}</td>
                    <td className={item.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>{item.tipo}</td>
                    <td className="max-w-[360px] truncate" title={item.descricao}>{item.descricao}</td>
                    <td>{item.status}</td>
                    <td className={Number(item.dias_em_aberto || 0) > 0 ? 'text-danger-700' : 'text-gray-700'}>{item.dias_em_aberto}</td>
                    <td>{FAIXAS.find((f) => f.key === item.faixa)?.label || item.faixa}</td>
                    <td className={item.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>
                      R$ {formatarValor(item.valor)}
                    </td>
                  </tr>
                ))}
                {(dados.itens || []).length === 0 && !carregando && (
                  <tr>
                    <td colSpan={7} className="text-center text-gray-500 py-8">
                      Nenhum item encontrado para os filtros selecionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgingPagarReceber;
